import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, FileEdit, Send, CheckCircle, XCircle, TrendingUp } from 'lucide-react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { DateRangePicker } from 'rsuite';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

// Registrar componentes de Chart.js
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const Dashboard = () => {
    const navigate = useNavigate();
    const { tienePermiso } = useAuth();
    const verPrecios = tienePermiso('ver_detalles_costos');
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [filtroRango, setFiltroRango] = useState('30'); // 7, 30, custom
    const [dateRange, setDateRange] = useState(null);
    const chartRef = useRef(null);
    const [hiddenDatasets, setHiddenDatasets] = useState(new Set());

    useEffect(() => {
        handleFiltroChange('30');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const cargarStats = async (desde = null, hasta = null) => {
        setLoading(true);
        try {
            const params = {};
            if (desde) params.desde = desde;
            if (hasta) params.hasta = hasta;

            const response = await api.dashboard.getStats(params);
            setStats(response);
        } catch (err) {
            console.error('Error al cargar estadísticas:', err);
            toast.error('Error al cargar estadísticas');
        } finally {
            setLoading(false);
        }
    };

    const handleFiltroChange = (rango) => {
        setFiltroRango(rango);

        if (rango === 'custom') return;

        const hoy = new Date();
        const desde = new Date();
        desde.setDate(hoy.getDate() - parseInt(rango));

        setDateRange([desde, hoy]);
        const desdeStr = desde.toISOString().split('T')[0];
        const hastaStr = hoy.toISOString().split('T')[0];
        cargarStats(desdeStr, hastaStr);
    };

    const handleDateRangeChange = (value) => {
        setDateRange(value);
        if (value && value[0] && value[1]) {
            const desdeStr = value[0].toISOString().split('T')[0];
            const hastaStr = value[1].toISOString().split('T')[0];
            cargarStats(desdeStr, hastaStr);
        }
    };

    const formatCurrency = (amount, moneda = 'PEN') => {
        const symbol = moneda === 'USD' ? '$ ' : 'S/ ';
        return symbol + parseFloat(amount || 0).toLocaleString('es-PE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const priceClass = verPrecios ? '' : 'price-hidden';
    const blurPrice = (value, moneda) => verPrecios ? formatCurrency(value, moneda) : '•••••';

    const formatDate = (dateStr) => {
        const [y, m, d] = dateStr.split('-');
        return `${d}/${m}`;
    };

    // Generar historial con todas las fechas del rango (sin saltos)
    const generarHistorialCompleto = () => {
        if (!stats?.historial) return [];

        let startDate, endDate;
        if (dateRange && dateRange[0] && dateRange[1]) {
            startDate = new Date(dateRange[0]);
            endDate = new Date(dateRange[1]);
        } else if (stats.historial.length > 0) {
            const fechas = stats.historial.map(h => new Date(h.fecha + 'T00:00:00'));
            startDate = new Date(Math.min(...fechas));
            endDate = new Date(Math.max(...fechas));
        } else {
            return [];
        }

        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);

        // Mapa de datos existentes por fecha YYYY-MM-DD
        const dataMap = {};
        stats.historial.forEach(h => {
            const key = typeof h.fecha === 'string' ? h.fecha.split('T')[0] : new Date(h.fecha).toISOString().split('T')[0];
            dataMap[key] = h;
        });

        // Generar todas las fechas en el rango
        const result = [];
        const current = new Date(startDate);
        while (current <= endDate) {
            const key = current.toISOString().split('T')[0];
            result.push(dataMap[key] || { fecha: key, total: 0, aprobadas: 0, enviadas: 0 });
            current.setDate(current.getDate() + 1);
        }

        return result;
    };

    const historialCompleto = generarHistorialCompleto();

    const legendItems = [
        { label: 'Total', color: 'rgba(140, 220, 255, 1)', datasetIndex: 0 },
        { label: 'Aprobadas', color: 'rgba(52, 211, 153, 1)', datasetIndex: 1 },
        { label: 'Enviadas', color: 'rgba(96, 165, 250, 1)', datasetIndex: 2 }
    ];

    const toggleDataset = (index) => {
        const chart = chartRef.current;
        if (!chart) return;
        const meta = chart.getDatasetMeta(index);
        meta.hidden = !meta.hidden;
        chart.update();
        setHiddenDatasets(prev => {
            const next = new Set(prev);
            if (next.has(index)) next.delete(index);
            else next.add(index);
            return next;
        });
    };

    // Configuración del gráfico
    const chartData = {
        labels: historialCompleto.map(h => formatDate(typeof h.fecha === 'string' ? h.fecha.split('T')[0] : new Date(h.fecha).toISOString().split('T')[0])),
        datasets: [
            {
                label: 'Total',
                data: historialCompleto.map(h => h.total),
                borderColor: 'rgba(140, 220, 255, 1)',
                backgroundColor: 'rgba(140, 220, 255, 0.1)',
                fill: true,
                tension: 0.4
            },
            {
                label: 'Aprobadas',
                data: historialCompleto.map(h => h.aprobadas),
                borderColor: 'rgba(52, 211, 153, 1)',
                backgroundColor: 'rgba(52, 211, 153, 0.1)',
                fill: false,
                tension: 0.4
            },
            {
                label: 'Enviadas',
                data: historialCompleto.map(h => h.enviadas),
                borderColor: 'rgba(96, 165, 250, 1)',
                backgroundColor: 'rgba(96, 165, 250, 0.1)',
                fill: false,
                tension: 0.4
            }
        ]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                backgroundColor: 'rgba(20, 30, 60, 0.95)',
                titleColor: '#fff',
                bodyColor: 'rgba(255, 255, 255, 0.8)',
                borderColor: 'rgba(140, 220, 255, 0.3)',
                borderWidth: 1,
                padding: 12,
                cornerRadius: 8
            }
        },
        scales: {
            x: {
                grid: {
                    color: 'rgba(255, 255, 255, 0.05)'
                },
                ticks: {
                    color: 'rgba(255, 255, 255, 0.5)',
                    maxTicksLimit: 15,
                    maxRotation: 0
                }
            },
            y: {
                beginAtZero: true,
                grid: {
                    color: 'rgba(255, 255, 255, 0.05)'
                },
                ticks: {
                    color: 'rgba(255, 255, 255, 0.5)',
                    stepSize: 1
                }
            }
        }
    };

    if (loading && !stats) {
        return <div className="loading">Cargando dashboard...</div>;
    }

    const contadores = stats?.contadores || {};
    const filtroTotales = stats?.filtroTotales || {};

    return (
        <div className="dashboard">
            {/* Header */}
            <div className="dashboard-header">
                <h2>Dashboard</h2>
            </div>

            {/* Contadores globales (sin filtro de fecha) */}
            <div className="dashboard-counters">
                <div className="counter-card total">
                    <div className="counter-icon">
                        <FileText size={24} />
                    </div>
                    <div className="counter-info">
                        <span className="counter-value">{contadores.total || 0}</span>
                        <span className="counter-label">Total Cotizaciones</span>
                    </div>
                </div>

                <div className="counter-card borrador">
                    <div className="counter-icon">
                        <FileEdit size={24} />
                    </div>
                    <div className="counter-info">
                        <span className="counter-value">{contadores.borradores || 0}</span>
                        <span className="counter-label">Borradores</span>
                    </div>
                </div>

                <div className="counter-card enviada">
                    <div className="counter-icon">
                        <Send size={24} />
                    </div>
                    <div className="counter-info">
                        <span className="counter-value">{contadores.enviadas || 0}</span>
                        <span className="counter-label">Enviadas</span>
                    </div>
                </div>

                <div className="counter-card aprobada">
                    <div className="counter-icon">
                        <CheckCircle size={24} />
                    </div>
                    <div className="counter-info">
                        <span className="counter-value">{contadores.aprobadas || 0}</span>
                        <span className="counter-label">Aprobadas</span>
                    </div>
                </div>

                <div className="counter-card rechazada">
                    <div className="counter-icon">
                        <XCircle size={24} />
                    </div>
                    <div className="counter-info">
                        <span className="counter-value">{contadores.rechazadas || 0}</span>
                        <span className="counter-label">Rechazadas</span>
                    </div>
                </div>

                <div className="counter-card conversion">
                    <div className="counter-icon">
                        <TrendingUp size={24} />
                    </div>
                    <div className="counter-info">
                        <span className="counter-value">{contadores.tasaConversion || 0}%</span>
                        <span className="counter-label">Tasa Conversión</span>
                    </div>
                </div>
            </div>

            {/* Monto aprobado global */}
            <div className="dashboard-monto">
                <div className="monto-card">
                    <span className="monto-label">Monto Total Aprobado</span>
                    <span className={`monto-value ${priceClass}`}>{blurPrice(contadores.monto_aprobado)}</span>
                </div>
            </div>

            {/* Gráfico y tabla */}
            <div className="dashboard-content">
                {/* Gráfico */}
                <div className="dashboard-chart card">
                    <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                        <h3>Historial de Cotizaciones</h3>
                        <div className="dashboard-filters">
                            <div className="filter-buttons">
                                <button
                                    className={`filter-btn ${filtroRango === '7' ? 'active' : ''}`}
                                    onClick={() => handleFiltroChange('7')}
                                >
                                    7 días
                                </button>
                                <button
                                    className={`filter-btn ${filtroRango === '30' ? 'active' : ''}`}
                                    onClick={() => handleFiltroChange('30')}
                                >
                                    30 días
                                </button>
                                <button
                                    className={`filter-btn ${filtroRango === 'custom' ? 'active' : ''}`}
                                    onClick={() => setFiltroRango('custom')}
                                >
                                    Personalizado
                                </button>
                            </div>
                            {filtroRango === 'custom' && (
                                <div className="filter-custom">
                                    <DateRangePicker
                                        value={dateRange}
                                        onChange={handleDateRangeChange}
                                        format="dd/MM/yyyy"
                                        character=" - "
                                        placeholder="Seleccionar rango"
                                        showOneCalendar
                                        placement="bottomEnd"
                                        className="rs-theme-dark"
                                        ranges={[]}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="card-body">
                        <div className="chart-legend">
                            {legendItems.map(item => (
                                <div
                                    key={item.datasetIndex}
                                    className={`chart-legend-item ${hiddenDatasets.has(item.datasetIndex) ? 'dimmed' : ''}`}
                                    onClick={() => toggleDataset(item.datasetIndex)}
                                >
                                    <span
                                        className="chart-legend-dot"
                                        style={{ backgroundColor: hiddenDatasets.has(item.datasetIndex) ? 'rgba(100,100,100,0.3)' : item.color }}
                                    />
                                    {item.label}
                                </div>
                            ))}
                        </div>
                        <div className="chart-container">
                            {historialCompleto.length > 0 ? (
                                <Line ref={chartRef} data={chartData} options={chartOptions} />
                            ) : (
                                <div className="empty-state">
                                    <p>No hay datos para el período seleccionado</p>
                                </div>
                            )}
                        </div>

                        {/* Resumen del período filtrado */}
                        <div className="chart-summary">
                            <div className="chart-summary-item">
                                <span className="chart-summary-value" style={{ color: 'rgba(140, 220, 255, 1)' }}>{filtroTotales.total || 0}</span>
                                <span className="chart-summary-label">Total</span>
                            </div>
                            <div className="chart-summary-item">
                                <span className="chart-summary-value" style={{ color: 'rgba(96, 165, 250, 1)' }}>{filtroTotales.enviadas || 0}</span>
                                <span className="chart-summary-label">Enviadas</span>
                            </div>
                            <div className="chart-summary-item">
                                <span className="chart-summary-value" style={{ color: 'rgba(52, 211, 153, 1)' }}>{filtroTotales.aprobadas || 0}</span>
                                <span className="chart-summary-label">Aprobadas</span>
                            </div>
                            <div className="chart-summary-item">
                                <span className={`chart-summary-value monto ${priceClass}`} style={{ color: '#34d399' }}>{blurPrice(filtroTotales.monto_aprobado)}</span>
                                <span className="chart-summary-label">Monto Aprobado</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Últimas cotizaciones */}
                <div className="dashboard-recent card">
                    <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h3>Últimas Cotizaciones</h3>
                        <button className="btn ghost small" onClick={() => navigate('/cotizaciones')}>
                            Ver todas
                        </button>
                    </div>
                    <div className="card-body">
                        {stats?.ultimas?.length > 0 ? (
                            <div className="recent-list">
                                {stats.ultimas.map((cot) => (
                                    <div
                                        key={cot.id}
                                        className="recent-item"
                                        onClick={() => navigate(`/cotizaciones/${cot.id}`)}
                                    >
                                        <div className="recent-info">
                                            <span className="recent-numero">{cot.numero}</span>
                                            <span className="recent-cliente">{cot.cliente_nombre}</span>
                                        </div>
                                        <div className="recent-meta">
                                            <span className={`badge estado-${cot.estado}`}>
                                                {cot.estado}
                                            </span>
                                            <span className={`recent-total ${priceClass}`}>
                                                {blurPrice(cot.total, cot.moneda)}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="empty-state">
                                <p>No hay cotizaciones recientes</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
